import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Cake as CakeIcon,
  CardMembership as MembershipIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  VolunteerActivism as VolunteerIcon,
} from '@mui/icons-material';

const ConfirmationDialog = ({ open, onClose, onConfirm, formData, isSubmitting }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getMembershipLabel = (type) => {
    switch (type) {
      case 'vrijwillig':
        return 'Vrijwillige bijdrage (ook lid van PvdA/GroenLinks)';
      case 'standaard':
        return 'Standaard lidmaatschap (€35,-)';
      case 'korting':
        return 'Met leeftijdskorting (€20,-)';
      default:
        return type;
    }
  };

  const getSelectedActivities = () => {
    const activities = [];
    if (formData.activiteiten.communicatie) activities.push('Communicatie/marketing');
    if (formData.activiteiten.ict) activities.push('ICT werkzaamheden');
    if (formData.activiteiten.bestuurswerk) activities.push('Bestuurswerk');
    if (formData.activiteiten.fractielidmaatschap) activities.push('(Steun)fractielidmaatschap');
    if (formData.activiteiten.professioneleKennis) activities.push('Professionele kennis inbrengen');
    if (formData.activiteiten.campagne) activities.push('Campagne activiteiten');
    if (formData.activiteiten.anders && formData.andereActiviteiten) {
      activities.push(`Anders: ${formData.andereActiviteiten}`);
    }
    return activities;
  };

  const InfoRow = ({ icon, label, value }) => (
    value ? (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ color: '#1976d2', mr: 2, mt: 0.5 }}>{icon}</Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="body1" sx={{ mt: 0.5 }}>
            {value}
          </Typography>
        </Box>
      </Box>
    ) : null
  );

  const selectedActivities = getSelectedActivities();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #e53935 0%, #4caf50 100%)',
          color: 'white',
          fontSize: '1.5rem',
          fontWeight: 700,
          py: 3,
        }}
      >
        Bevestig uw aanmelding
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Typography variant="body1" sx={{ mb: 3, color: '#555' }}>
          Controleer uw gegevens voordat u de aanmelding verzendt:
        </Typography>

        {/* Personal Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#e53935', fontWeight: 600 }}>
            <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Persoonlijke Gegevens
          </Typography>
          <InfoRow icon={<PersonIcon fontSize="small" />} label="Naam" value={formData.naam} />
          <InfoRow icon={<HomeIcon fontSize="small" />} label="Adres" value={formData.adres} />
          <InfoRow icon={<CakeIcon fontSize="small" />} label="Geboortedatum" value={formatDate(formData.geboortedatum)} />
          <InfoRow icon={<PhoneIcon fontSize="small" />} label="Telefoon" value={formData.telefoon} />
          <InfoRow icon={<EmailIcon fontSize="small" />} label="E-mail" value={formData.email} />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Membership */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#4caf50', fontWeight: 600 }}>
            <MembershipIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Lidmaatschap
          </Typography>
          <InfoRow
            icon={<MembershipIcon fontSize="small" />}
            label="Type"
            value={getMembershipLabel(formData.lidmaatschap)}
          />
          {formData.lidmaatschap === 'vrijwillig' && (
            <InfoRow
              icon={<MembershipIcon fontSize="small" />}
              label="Vrijwillige bijdrage"
              value={`€${formData.vrijwilligeBijdrage || '0'}`}
            />
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Background Information */}
        {(formData.opleidingsachtergrond || formData.professie || formData.vrijwilligerswerk || formData.politiekeErvaring || formData.interessegebied) && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
                <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Achtergrond
              </Typography>
              <InfoRow icon={<SchoolIcon fontSize="small" />} label="Opleiding" value={formData.opleidingsachtergrond} />
              {formData.studerend === 'ja' && (
                <InfoRow icon={<SchoolIcon fontSize="small" />} label="Studierichting" value={formData.studierichting} />
              )}
              <InfoRow icon={<WorkIcon fontSize="small" />} label="Professie" value={formData.professie} />
              <InfoRow icon={<VolunteerIcon fontSize="small" />} label="Vrijwilligerswerk" value={formData.vrijwilligerswerk} />
              <InfoRow icon={<PersonIcon fontSize="small" />} label="Politieke ervaring" value={formData.politiekeErvaring} />
              <InfoRow icon={<PersonIcon fontSize="small" />} label="Interessegebied" value={formData.interessegebied} />
            </Box>
            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* Activities */}
        {selectedActivities.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#ff9800', fontWeight: 600 }}>
              <VolunteerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Gewenste Activiteiten
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
              {selectedActivities.map((activity, index) => (
                <Chip
                  key={index}
                  label={activity}
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#f5f5f5' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isSubmitting}
          sx={{ mr: 1 }}
        >
          Annuleren
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          sx={{
            px: 4,
            py: 1.5,
            fontWeight: 600,
          }}
        >
          {isSubmitting ? 'Verzenden...' : 'Bevestigen en Verzenden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
